import { Alert, Button, Form } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageFor } from '@/lib/api/index.js';
import { space } from '@/design/tokens.js';
import { AuthLayout } from './AuthLayout.js';
import { PasswordField } from './PasswordField.js';
import { useRotateExpiredPassword } from './account.api.js';

export interface PasswordExpiredPageProps {
  email: string;
  onCancel: () => void;
}

/**
 * Shown when sign-in succeeded but the password has passed its 90-day life.
 *
 * Presented as a task to finish, not as a failure: the credentials were
 * correct, and the user is one field away from being in. The email is carried
 * over from the sign-in attempt so they do not retype it, and the current
 * password is asked for again because this route is public.
 */
export function PasswordExpiredPage({ email, onCancel }: PasswordExpiredPageProps) {
  const navigate = useNavigate();
  const rotate = useRotateExpiredPassword();
  const [error, setError] = useState<string | null>(null);

  const onFinish = async (values: { currentPassword: string; newPassword: string }) => {
    setError(null);
    try {
      await rotate.mutateAsync({ email, ...values });
      navigate('/', { replace: true });
    } catch (cause) {
      setError(messageFor(cause));
    }
  };

  return (
    <AuthLayout
      title="Time for a new password"
      subtitle={`Passwords expire every 90 days. Set a new one for ${email} to continue.`}
      footer={
        <Button type="link" onClick={onCancel}>
          Use a different account
        </Button>
      }
    >
      {error ? (
        <Alert type="error" showIcon title={error} style={{ marginBottom: space[4] }} />
      ) : null}

      <Form layout="vertical" requiredMark={false} onFinish={(v) => void onFinish(v)}>
        <PasswordField
          name="currentPassword"
          label="Current password"
          autoComplete="current-password"
          showRules={false}
          autoFocus
        />
        <PasswordField name="newPassword" label="New password" />
        <Button type="primary" htmlType="submit" size="large" block loading={rotate.isPending}>
          Set new password
        </Button>
      </Form>
    </AuthLayout>
  );
}
